import {
  Button,
  FormField,
  Label,
  Modal,
  TextInput,
} from '@dev-spendesk/grapes';
import { type FormikProps } from 'formik';
import React, { useMemo } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { ColumnDisplayOptionsContainer } from './ColumnDisplayOptionsContainer';
import { CommonEditComponents } from './CommonEditComponents';
import type { TypeFormValues } from './form';
import { type RowDisplayOption } from '../../../export';

type Props = {
  isOpen: boolean;
  title: string;
  description: string;
  isDeletable: boolean;
  onRemoveColumn: () => void;
  onClose: () => void;
  rowDisplayOptions?: RowDisplayOption[];
} & FormikProps<TypeFormValues>;

export const TypeColumnEditModal = ({
  isOpen,
  title,
  description,
  isDeletable,
  onRemoveColumn,
  onClose,
  // Formik
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  handleSubmit,
  rowDisplayOptions,
  ...rest
}: Props) => {
  const { t } = useTranslation('global');

  const showColumnDisplayOptions = useMemo(() => {
    return (
      rowDisplayOptions &&
      rowDisplayOptions.some((rowDisplayOption) => rowDisplayOption.active)
    );
  }, [rowDisplayOptions]);

  const noColumnDisplaySelected =
    rowDisplayOptions &&
    values.displayOptions &&
    !values.displayOptions.expense.active &&
    !values.displayOptions.analytical.active &&
    !values.displayOptions.vat.active &&
    !values.displayOptions.supplier.active;

  return (
    <Modal
      isOpen={isOpen}
      title={t('exports.columnEditModalTitle', { title })}
      subtitle={description}
      onClose={onClose}
      iconName="pen"
      actions={[
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={t('misc.cancel')}
          onClick={onClose}
        />,
        <Button
          key="saveChanges"
          text={t('misc.saveChanges')}
          isDisabled={noColumnDisplaySelected}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={(event: any) => {
            handleSubmit(event);
          }}
        />,
      ]}
    >
      <div className="text-left">
        <CommonEditComponents
          values={values}
          handleChange={handleChange}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
          handleSubmit={handleSubmit}
          {...rest}
        />

        <div className="mt-24 flex flex-col">
          <Label
            className="mb-8"
            label={t('bookkeep.settings.export.typeOptions.header')}
          />
          {/* General */}
          <FormField
            label={t('bookkeep.settings.export.typeOptions.general')}
            className="mb-8"
            alertMessage={
              touched.general && Boolean(errors.general)
                ? errors.general
                : undefined
            }
          >
            <TextInput
              name="typeGeneral"
              id="typeGeneral"
              value={values.general || ''}
              onChange={(e) => {
                handleChange(e);
                setFieldValue('general', e.target.value);
              }}
              isInvalid={touched.general && Boolean(errors.general)}
            />
          </FormField>

          {/* Analytical */}
          <FormField
            label={t('bookkeep.settings.export.typeOptions.analytical')}
            className="mb-8"
            alertMessage={
              touched.analytical && Boolean(errors.analytical)
                ? errors.analytical
                : undefined
            }
          >
            <TextInput
              name="typeAnalytical"
              id="typeAnalytical"
              value={values.analytical || ''}
              onChange={(e) => {
                handleChange(e);
                setFieldValue('analytical', e.target.value);
              }}
              isInvalid={touched.analytical && Boolean(errors.analytical)}
            />
          </FormField>
        </div>

        {showColumnDisplayOptions && (
          <FormField
            label={t(
              'bookkeep.settings.export.columnDisplayOptions.headerFormLabel',
            )}
            className="mt-24"
            alertMessage={
              noColumnDisplaySelected
                ? t(
                    `bookkeep.settings.export.columnDisplayOptions.selectColumnAlert`,
                  )
                : ''
            }
          >
            <ColumnDisplayOptionsContainer
              displayOptions={values.displayOptions}
              onDisplayOptionChange={(upd) =>
                setFieldValue('displayOptions', upd)
              }
              rowDisplayOptions={rowDisplayOptions ?? []}
              isAnalyticalColumn={false}
            />
          </FormField>
        )}

        {isDeletable && (
          <Button
            hasNegativeMargins
            className="mt-24"
            variant="tertiaryNeutral"
            iconName="circle-minus"
            iconPosition="left"
            text={t('exports.removeColumn')}
            onClick={onRemoveColumn}
          />
        )}
      </div>
    </Modal>
  );
};
